import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  isRouteErrorResponse,
  useLoaderData,
  useRouteLoaderData,
  useLocation
} from "@remix-run/react";
import type {ClientLoaderFunctionArgs} from "@remix-run/react";
import { json } from "@remix-run/node";
import { useEffect, useState, useRef } from "react";
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/solid';
import tailwindStyle from "~/tailwind.css?url";
import rootStyle from "~/styles/root_styles.css?url";
import autoCompleteStyle from '@algolia/autocomplete-theme-classic/dist/theme.min.css?url';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';

import Navbar from "./components/navbar";
import Footer from "./components/footer";
import * as gtag from "./client/gtags.client";

export const links = () => [
  { rel: "icon", href: "/favicon.ico?v=1" },
  { rel: "stylesheet", href: tailwindStyle, as: 'style' },
  { rel: "stylesheet", href: rootStyle, as: 'style' },
  { rel: "stylesheet", href: autoCompleteStyle, as: 'style' }
];

// Load the GA tracking id from the .env
export const loader = async () => {
  return json({ gaTrackingId: process.env.GA_TRACKING_ID });
};

// loader values here don't change
// https://remix.run/docs/en/main/route/should-revalidate#never-reloading-the-root
export const shouldRevalidate = () => false;

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  // https://remix.run/docs/en/main/file-conventions/root#layout-export
  // https://github.com/remix-run/remix/issues/8951
  const loaderData = useRouteLoaderData<typeof loader>("root");
  const gaTrackingId = loaderData?.gaTrackingId;

  useEffect(() => {
    // console.log(`gaTrackingId: ${gaTrackingId}`)
    if (gaTrackingId?.length) {
      gtag.pageview(location.pathname, gaTrackingId);
    }
  }, [location, gaTrackingId]);

  return (
    <html lang="en" className="text-rr-primary">
      <head>
        {/* https://github.com/remix-run/examples/blob/main/google-analytics/app/root.tsx */}
        {/* process.env.NODE_ENV === "development" ||  */}
        {process.env.NODE_ENV === "development" || !gaTrackingId ? null : (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`}
            />
            <script
              async
              id="gtag-init"
              dangerouslySetInnerHTML={{
                __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', '${gaTrackingId}', {
                  page_path: window.location.pathname,
                });
              `
              }}
            />
          </>
        )}

        {/* https://developers.google.com/search/docs/appearance/site-names#website */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
            {
              "@context" : "https://schema.org",
              "@type" : "WebSite",
              "name" : "Related Repos",
              "url" : "https://relatedrepos.com/"
            }`}}
        />

        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:site_name" content="Related Repos" />
        <Meta />
        <Links />
      </head>
      <body>
        <div style={{ maxWidth: "1200px", margin: "auto" }}>
          <Navbar />
          <main className="pl-2 pr-2">
            {children}
          </main>
          <Footer />
        </div>

        <ToastContainer />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary() {
  const error: any = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </>
    );
  }

  return (
    <>
      <h1>Error!</h1>
      <p>{error?.status ?? "Unknown status"}</p>
      <p>{error?.statusText ?? "Unknown statusText"}</p>
      {/* only show error message in development */}
      <p>{process.env.NODE_ENV === "development" ? (error?.message ?? "Unknown error") : null}</p>
    </>
  );
}